import { routesMap } from 'app/Router';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import { RevxButton } from 'components/RevxButton';
import RevxFlexLayout from 'components/RevxFlexLayout';
import { RevxGalleryImg } from 'components/RevxGalleryImg';
import { RevxInputNumber } from 'components/RevxInputNumber';
import { RevxSpin } from 'components/RevxSpin';
import {
  Paragraph,
  SectionHeading,
  UnderlineText,
} from 'components/RevxTypography';
import {
  BUSINESS_TYPE_ENUM,
  GALLERY_TYPE_ENUM,
  SkuGalleryLabel,
} from 'domains/gallerySkuDomain';
import { JobConfigSourceEnum } from 'domains/jobconfigDomain';
import { resolveGallerySupportOrg } from 'domains/orgDomain';
import { useCanvaMatchView } from 'hooks';
import { useCanvaPublishDraft } from 'hooks/useCanvaPublishDraft';
import { useOrg } from 'hooks/useOrg';
import { useSsoBuyerExperience } from 'hooks/useSsoBuyerExperience';
import { useUser } from 'hooks/useUser';
import { sortBy } from 'lodash';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router';
import { JOB_MAX_QUANTITY } from 'utils/constants';
import { startJobConfigWithGalleryLabel } from 'utils/gallery';
import { isBuyer } from 'utils/rbac';
import './GalleryCard.less';

export const GalleryCard: React.FC<{
  businessType: BUSINESS_TYPE_ENUM;
}> = ({ businessType }) => {
  const dispatch = useAppDispatch();
  const history = useHistory();
  const galleryLabels = useAppSelector((state) => state.gallery.galleryLabels);
  const isLoading = useAppSelector(
    (state) => state.loading.effects.gallery.getGalleryLabels
  );
  const preConfiguredSkus = useAppSelector(
    (state) => state.gallery.preConfiguredSkus
  );

  // A Canva buyer shops like an ordinary user here -- see useSsoBuyerExperience.
  const isSsoBuyerExperience = useSsoBuyerExperience();
  // A visit carrying a parked Canva design finishes the publish here instead of starting an
  // empty job config: the design already decides everything the category step would ask for.
  const { isCanvaPublish, publishWithSku } = useCanvaPublishDraft();
  const { openCategory } = useCanvaMatchView();

  /**
   * Canva's version of opening a category. The publish needs one SKU, so a category holding
   * exactly one is published straight away and the buyer never sees a step with a single
   * choice on it; anything else opens the picker. A category with nothing in it falls back to
   * the ordinary flow rather than leaving the click doing nothing.
   *
   * @returns whether the click was handled here
   */
  const openCategoryForCanva = async (galleryLabelId: number) => {
    // The gallery list's query, not the booklet wizard's: it widens the category to its
    // sub-categories, which is where a top-level category's products usually live. Counting
    // with the narrower one reports zero for those and sends the buyer down the old path.
    const paged = await dispatch.gallery.getCompanySkusByLabel({
      galleryLabelId,
      page: 1,
      size: 1000,
    });
    const skus = paged?.content ?? [];
    if (skus.length === 1) {
      await publishWithSku(Number(skus[0].id));
      return true;
    }
    if (skus.length > 1) {
      openCategory(galleryLabelId);
      return true;
    }
    return false;
  };

  const filteredGalleryLabels = useMemo(() => {
    let labels = [];

    if (isSsoBuyerExperience) {
      labels = galleryLabels.filter((item) => !item.skuLabel.parentId);
    } else {
      labels = galleryLabels.filter(
        (item) =>
          !item.skuLabel.parentId &&
          item.skuCount > 0 &&
          item.skuLabel.active &&
          !item.skuLabel.businessTypes.includes(BUSINESS_TYPE_ENUM.MY_COMPANY)
      );
      if (businessType) {
        labels = labels.filter((item) => {
          return item.skuLabel.businessTypes.includes(businessType);
        });
      }
    }

    // sort by name
    labels = sortBy(
      [
        ...labels,
        ...preConfiguredSkus.map((item) => {
          return {
            skuCount: -1,
            skuLabel: {
              ...item,
              id: -1,
              skuId: item.id,
              galleryLabels: [],
              dropDownLabels: [],
              parentId: -1,
              summary: '',
            },
          };
        }),
      ],
      [
        (item) =>
          isSsoBuyerExperience &&
          (item.skuLabel as SkuGalleryLabel).galleryType ===
            GALLERY_TYPE_ENUM.COMPANY
            ? 0
            : 1,
        (item) => item.skuLabel.name,
      ]
    );

    return labels;
  }, [businessType, galleryLabels, isSsoBuyerExperience, preConfiguredSkus]);

  return (
    <RevxSpin spinning={isSsoBuyerExperience && isLoading}>
      <div className="gallery-card-grid">
        {filteredGalleryLabels.map((item, index) => {
          const skuLabel = item.skuLabel;
          return (
            <>
              {skuLabel.id !== -1 ? (
                <GalleryCardItem
                  key={`${skuLabel.id}${index}`}
                  item={{
                    id: skuLabel.id,
                    name: skuLabel.name,
                    image: skuLabel.image,
                    description: skuLabel.description,
                  }}
                  onClick={async () => {
                    if (isCanvaPublish) {
                      if (await openCategoryForCanva(skuLabel.id)) {
                        return;
                      }
                    }
                    if (isSsoBuyerExperience) {
                      // Logic for SSO Buyer click
                      const label = skuLabel as SkuGalleryLabel;

                      console.log('SSO Buyer clicked label:', label);
                      if (label.galleryType === GALLERY_TYPE_ENUM.COMPANY) {
                        console.log('Company gallery label clicked:', label);
                        dispatch.companyGallery.setSelectedSkuType({
                          id: label.id,
                          name: label.name,
                          images: [label.image],
                        });
                        history.push(
                          `${routesMap.ssoBuyerShop.path}?parentId=${label.id}`
                        );
                      } else if (label.companyLabelId) {
                        dispatch.companyGallery.setSelectedSkuType({
                          id: label.companyLabelId,
                          name: label.name,
                          images: [label.image],
                        });
                        history.push(
                          routesMap.ssoBuyerCompanyGallery.path.replace(
                            ':id',
                            String(label.id)
                          )
                        );
                      } else if (label.galleryLabelId) {
                        const nextLabel = {
                          ...item,
                          skuLabel: {
                            ...item.skuLabel,
                            id: label.id,
                          },
                        };
                        dispatch.jobConfig.setJobconfigSource(
                          JobConfigSourceEnum.GALLERY
                        );
                        startJobConfigWithGalleryLabel(
                          dispatch,
                          nextLabel,
                          history
                        );
                      }
                    } else {
                      dispatch.jobConfig.setJobconfigSource(
                        JobConfigSourceEnum.GALLERY
                      );
                      startJobConfigWithGalleryLabel(dispatch, item, history);
                    }
                  }}
                />
              ) : (
                <GalleryCardPreConfigItem
                  key={`preConfig${skuLabel.image}${index}`}
                  item={{
                    ...skuLabel,
                    id: skuLabel.id,
                    name: skuLabel.name,
                    image: skuLabel.image,
                    description: skuLabel.description,
                    skuId: skuLabel.skuId,
                  }}
                />
              )}
            </>
          );
        })}
        {isSsoBuyerExperience && !isLoading && <GalleryTipItem />}
      </div>
      {isSsoBuyerExperience && !isLoading && (
        <div style={{ marginTop: '24px' }}>
          <RevxButton
            size="large"
            type="primary"
            style={{ width: '270px' }}
            onClick={() => history.push('/workspace')}
          >
            Manage Orders
          </RevxButton>
        </div>
      )}
    </RevxSpin>
  );
};

const GalleryTipItem: React.FC = () => {
  const org = useOrg();
  const orgList = useAppSelector((state) => state.org.list);
  const supportOrg = resolveGallerySupportOrg(org, orgList);
  const email = supportOrg?.gallerySupportEmail?.trim();
  const phone = supportOrg?.phone1?.trim();

  if (!email && !phone) {
    return null;
  }

  return (
    <div
      className="gallery-item-tip"
      style={{
        background: '#EAEAEA',
        borderRadius: '10px',
        padding: '24px',
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        height: '315px',
        width: '270px',
      }}
    >
      <Paragraph
        style={{
          fontSize: '16px',
          fontWeight: 400,
          marginBottom: '0',
          color: '#666',
        }}
      >
        Don’t see what you need?
      </Paragraph>
      <Paragraph
        style={{
          fontSize: '16px',
          fontWeight: 400,
          marginBottom: '24px',
          color: '#666',
        }}
      >
        Contact us for a quote on your next project.
      </Paragraph>

      {phone && (
        <Paragraph style={{ color: '#4B669D', marginBottom: '0' }}>
          {phone}
        </Paragraph>
      )}
      {email && (
        <Paragraph style={{ color: '#4B669D', marginBottom: '0' }}>
          <a href={`mailto:${email}`} style={{ color: '#4B669D' }}>
            {email}
          </a>
        </Paragraph>
      )}
    </div>
  );
};

const GalleryCardPreConfigItem: React.FC<{
  item: {
    id: number;
    name: string;
    image: string;
    description: string;
    skuId?: string;
    defaultQty?: number;
    unitPriceConfigured?: boolean;
  };
}> = ({ item }) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [quantity, setQuantity] = useState(Number(item.defaultQty || 100));
  const [addToCartloading, setAddToCartloading] = useState<boolean>(false);
  const [comparisonloading, setComparisonloading] = useState<boolean>(false);
  const history = useHistory();
  const user = useUser();
  return (
    <div className="gallery-item" key={`preConfig${item.id}${item.image}`}>
      <div className="gallery-item-img">
        {item.image ? (
          <RevxGalleryImg src={item.image} />
        ) : (
          <span className="img"></span>
        )}
      </div>
      <SectionHeading
        className="gallery-item-type-name"
        ellipsis={{
          tooltip: item.name,
        }}
      >
        {item.name}
      </SectionHeading>
      {/* <div className="gallery-item-desc">{item.description || ' '}</div> */}
      <RevxFlexLayout style={{ marginBottom: '6px' }}>
        <RevxFlexLayout justify="justify-center">
          <label style={{ paddingRight: '6px', fontWeight: 500 }}>
            {t('common.quantityAbbrev')}:{' '}
          </label>
          <RevxInputNumber
            style={{ height: '28px', maxWidth: '90px' }}
            isInteger
            min={1}
            max={JOB_MAX_QUANTITY}
            value={quantity}
            onChange={(e) => setQuantity(e)}
          />
        </RevxFlexLayout>
        <RevxFlexLayout
          justify="justify-center"
          hidden={item.unitPriceConfigured}
        >
          <RevxButton
            type="link"
            style={{ textDecoration: 'underline' }}
            hidden={isBuyer(user)}
            loading={comparisonloading}
            onClick={async () => {
              try {
                setComparisonloading(true);
                const jobConfig = await dispatch.jobConfig.startJobConfig({
                  skuId: Number(item.skuId),
                  quantity: quantity,
                  isExpert: false,
                });
                dispatch.jobConfig.setJobconfigSource(
                  JobConfigSourceEnum.GALLERY
                );
                history.replace(`${routesMap.jobConfig.path}/${jobConfig.id}`);
                dispatch.jobConfig.setIsDefaultComparison(true);
              } catch {
              } finally {
                setComparisonloading(false);
              }
            }}
          >
            <UnderlineText color="green">{t('common.compare')}</UnderlineText>
          </RevxButton>
        </RevxFlexLayout>
      </RevxFlexLayout>
      <RevxButton
        size="large"
        type="primary"
        loading={addToCartloading}
        onClick={async () => {
          try {
            setAddToCartloading(true);
            await dispatch.jobConfig.directlyAddToCart({
              skuId: Number(item.skuId),
              quantity: quantity,
              isExpert: false,
              directlyAddToCart: true,
            });
            history.push(routesMap.shoppingCart.path);
          } catch {
          } finally {
            setAddToCartloading(false);
          }
        }}
      >
        {t('gallery.skuList.addToCart')}
      </RevxButton>
    </div>
  );
};

const GalleryCardItem: React.FC<{
  item: {
    id: number;
    name: string;
    image: string;
    description: string;
  };
  onClick: () => void | Promise<void>;
}> = ({ item, onClick }) => {
  const { t } = useTranslation();
  const isSsoBuyer = useSsoBuyerExperience();
  // Opening a category can now mean "publish the parked design against its only product", which
  // is a round trip or two; without this the card looks inert while it happens.
  const [loading, setLoading] = useState(false);

  return (
    <div
      className="gallery-item"
      key={item.id}
      style={
        isSsoBuyer ? { justifyContent: 'flex-start', gap: '16px' } : undefined
      }
    >
      <div className="gallery-item-img">
        {item.image ? (
          <RevxGalleryImg src={item.image} />
        ) : (
          <span className="img"></span>
        )}
      </div>
      {!isSsoBuyer && (
        <Paragraph
          className="gallery-item-type-name"
          style={{ textAlign: 'center' }}
          ellipsis={{
            rows: 2,
            expandable: true,
            symbol: ' ',
            tooltip: `Create your own ${item.name}`,
          }}
        >
          {`${item.name}`}
        </Paragraph>
      )}
      {/* <div className="gallery-item-desc">{item.description || ' '}</div> */}
      <RevxButton
        size="large"
        type="primary"
        loading={loading}
        onClick={async () => {
          if (!onClick) {
            return;
          }
          try {
            setLoading(true);
            await onClick();
          } catch {
            // Reported by the request layer; all this handler owes the buyer is a card that
            // stops spinning and can be clicked again.
          } finally {
            setLoading(false);
          }
        }}
      >
        {isSsoBuyer
          ? `Create A ${item.name}`
          : t('gallery.skuList.get-started')}
      </RevxButton>
    </div>
  );
};

export { GalleryTipItem };
